// =============================================================================
// VandieZ admin-web -- Users search (/users)
// =============================================================================
// Search + paginate users by phone/name/primary role/status.  Row click
// opens the detail page for role-assign + suspend.  Designed for the
// support team who fields "I can't log in" / "please give me X role"
// requests every day.
// =============================================================================

import {
  AlertCircle,
  Ban,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Search,
  UserPlus,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { streamCsvDownload, usersAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { useToast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminUserDetail,
  AdminUserSummary,
  BulkSuspendUsersResponse,
  CreatableRole,
} from "@/types/rbac";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

const ROLE_OPTIONS = ["", "admin", "shipper", "driver", "owner", "agent"];
const STATUS_OPTIONS = ["", "active", "pending_kyc", "suspended", "banned"];

// Compact last-login renderer: time-only if it's today, otherwise the
// short date. Falls back to "Never" when the user hasn't logged in.
// The full timestamp is exposed via the cell's `title` tooltip.
function formatLastLogin(iso: string | null): string {
  if (iso === null) {return "Never";}
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {return iso;}
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? d.toLocaleTimeString() : d.toLocaleDateString();
}

function statusPillClass(status: string): string {
  switch (status) {
    case "active":
      return "bg-success-subtle text-success-subtle-foreground";
    case "suspended":
    case "banned":
      return "bg-danger-subtle text-danger-subtle-foreground";
    case "pending_kyc":
      return "bg-warning-subtle text-warning-subtle-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function UsersListPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  // URL is the source of truth for filter state. Operators can
  // bookmark a filtered view or share it via copy-paste.
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportingAll, setExportingAll] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSuspendOpen, setBulkSuspendOpen] = useState<boolean>(false);

  // Derive every committed filter + offset from the URL so refresh /
  // back / share preserves the operator's view.
  const q = searchParams.get("q") ?? "";
  const primaryRole = searchParams.get("primary_role") ?? "";
  const statusFilter = searchParams.get("status") ?? "";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;
  const sizeRaw = Number.parseInt(searchParams.get("size") ?? "", 10);
  const pageSize = PAGE_SIZE_OPTIONS.includes(
    sizeRaw as (typeof PAGE_SIZE_OPTIONS)[number],
  )
    ? sizeRaw
    : DEFAULT_PAGE_SIZE;

  // Filter form state (uncommitted until "Search" button or Enter).
  // Initialised from the URL so the form reflects the current view.
  const [qDraft, setQDraft] = useState<string>(q);
  const [roleDraft, setRoleDraft] = useState<string>(primaryRole);
  const [statusDraft, setStatusDraft] = useState<string>(statusFilter);

  // Helper: write a partial filter map into the URL, dropping empty
  // keys so the URL stays clean.
  const updateFilters = useCallback(
    (next: {
      q?: string;
      primary_role?: string;
      status?: string;
      offset?: number;
      size?: number;
    }): void => {
      setSearchParams(
        (cur) => {
          const params = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (
              v === undefined ||
              v === "" ||
              v === 0 ||
              (k === "size" && v === DEFAULT_PAGE_SIZE)
            ) {
              params.delete(k);
            } else {
              params.set(k, String(v));
            }
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await usersAdminApi.search(
          accessToken,
          {
            limit: pageSize,
            offset,
            ...(q ? { q } : {}),
            ...(primaryRole ? { primary_role: primaryRole } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // unmount
        } else {
          setError("Unexpected error. Try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, q, primaryRole, statusFilter, offset, pageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(controller.signal);
    return () => { controller.abort(); };
  }, [fetchPage]);

  // Drop the selection whenever the underlying query changes — picking
  // up rows from a different filter view would surprise the operator.
  useEffect(() => {
    setSelected(new Set());
  }, [q, primaryRole, statusFilter, offset]);

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && selected.size < items.length;

  const toggleRow = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const toggleAll = (): void => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      // Don't pre-tick already-suspended rows; they'd just be no-ops on
      // the server, no need to clutter the count.
      setSelected(
        new Set(items.filter((u) => u.status !== "suspended").map((u) => u.id)),
      );
    }
  };

  const selectedNames = useMemo(
    () =>
      items
        .filter((r) => selected.has(r.id))
        .slice(0, 3)
        .map((r) => r.full_name),
    [items, selected],
  );

  const onSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    updateFilters({
      q: qDraft,
      primary_role: roleDraft,
      status: statusDraft,
      offset: 0,
    });
  };

  const exportAllCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExportingAll(true);
    try {
      // Stream every matching user from the backend; no 200-row cap.
      const qsParts: string[] = [];
      if (q) {qsParts.push(`q=${encodeURIComponent(q)}`);}
      if (primaryRole) {qsParts.push(`primary_role=${encodeURIComponent(primaryRole)}`);}
      if (statusFilter) {qsParts.push(`status=${encodeURIComponent(statusFilter)}`);}
      const suffix = qsParts.length > 0 ? `?${qsParts.join("&")}` : "";
      const stamp = new Date().toISOString().slice(0, 10);
      await streamCsvDownload(
        accessToken,
        `/admin/exports/users.csv${suffix}`,
        `vandiez-users-${stamp}.csv`,
      );
      toast.success("Export started — check your downloads.");
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Export failed.";
      toast.error(msg);
    } finally {
      setExportingAll(false);
    }
  };

  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      // Pull up to 200 (server-capped) rows matching the current
      // filter set. Operators rarely need more for ad-hoc CSV dumps;
      // anything bigger should go through a proper analytics export.
      const res = await usersAdminApi.search(accessToken, {
        limit: 200,
        offset: 0,
        ...(q ? { q } : {}),
        ...(primaryRole ? { primary_role: primaryRole } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      const header = [
        "id",
        "phone",
        "full_name",
        "email",
        "primary_role",
        "status",
        "kyc_tier",
        "created_at",
        "last_login_at",
      ];
      const rows = res.items.map((u) => [
        u.id,
        u.phone,
        u.full_name,
        u.email ?? "",
        u.primary_role,
        u.status,
        u.kyc_tier,
        u.created_at,
        u.last_login_at ?? "",
      ]);
      const escape = (v: string): string => {
        // CSV quoting per RFC 4180: wrap in quotes, double-up any
        // existing quotes inside the field.
        if (/[",\n]/.test(v)) {
          return `"${v.replace(/"/g, '""')}"`;
        }
        return v;
      };
      const csv = [
        header.join(","),
        ...rows.map((r) => r.map((c) => escape(String(c))).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `vandiez-users-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        description="Search shippers, drivers, owners, admins. Manage roles and suspensions."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void exportCsv()}
              disabled={exporting || items.length === 0}
              title="First 200 rows of the current view"
            >
              <Download className="mr-1 h-4 w-4" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Button
              variant="outline"
              onClick={() => void exportAllCsv()}
              disabled={exportingAll}
              title="Stream every row matching the current filters (unlimited)"
            >
              <Download className="mr-1 h-4 w-4" />
              {exportingAll ? "Streaming…" : "Export all"}
            </Button>
            <Button onClick={() => { setCreateModalOpen(true); }}>
              <UserPlus className="mr-1 h-4 w-4" />
              New user
            </Button>
          </div>
        }
      />
        {createModalOpen && (
          <CreateAdminModal
            accessToken={accessToken}
            onClose={() => { setCreateModalOpen(false); }}
            onCreated={(detail) => {
              setCreateModalOpen(false);
              toast.success(`Admin created: ${detail.full_name}.`);
              navigate(`/users/${detail.id}`);
            }}
          />
        )}
        {bulkSuspendOpen && (
          <BulkSuspendModal
            accessToken={accessToken}
            userIds={Array.from(selected)}
            previewNames={selectedNames}
            onClose={() => { setBulkSuspendOpen(false); }}
            onDone={(res) => {
              setBulkSuspendOpen(false);
              setSelected(new Set());
              const parts: string[] = [];
              if (res.suspended > 0) {parts.push(`${res.suspended} suspended`);}
              if (res.already_suspended > 0) {parts.push(`${res.already_suspended} already suspended`);}
              if (res.not_found > 0) {parts.push(`${res.not_found} not found`);}
              if (res.skipped_self > 0) {parts.push("self filtered");}
              const summary = parts.length > 0 ? parts.join(", ") : "no rows changed";
              if (res.suspended > 0) {
                toast.success(`Bulk suspend: ${summary}.`);
              } else {
                toast.error(`Bulk suspend: ${summary}.`);
              }
              void fetchPage();
            }}
          />
        )}
        <Card>
          <CardHeader>
            <CardTitle>Search users</CardTitle>
            <CardDescription>
              Match against phone or full name. Filter by primary role / account
              status. Click a row to manage roles or suspend.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={onSearch}
              className="flex flex-wrap gap-3 items-end"
              data-testid="users-search-form"
            >
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Phone or name
                </label>
                <Input
                  value={qDraft}
                  onChange={(e) => { setQDraft(e.target.value); }}
                  placeholder="+91… or Rajesh"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Primary role
                </label>
                <select
                  value={roleDraft}
                  onChange={(e) => { setRoleDraft(e.target.value); }}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r || "any"} value={r}>
                      {r || "(any)"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Status
                </label>
                <select
                  value={statusDraft}
                  onChange={(e) => { setStatusDraft(e.target.value); }}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s || "any"} value={s}>
                      {s || "(any)"}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={loading}>
                <Search className="mr-1 h-4 w-4" /> Search
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Results</CardTitle>
            <CardDescription>
              {total > 0
                ? `Showing ${offset + 1}–${offset + items.length} of ${total}.`
                : "No matches yet."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {selected.size > 0 && (
              <div className="mb-3 flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selected.size} selected</span>
                  {selectedNames.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({selectedNames.join(", ")}
                      {selected.size > selectedNames.length ? ", …" : ""})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setSelected(new Set()); }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => { setBulkSuspendOpen(true); }}
                  >
                    <Ban className="mr-1 h-3 w-3" />
                    Suspend {selected.size}
                  </Button>
                </div>
              </div>
            )}

            {loading && items.length === 0 ? (
              <TableSkeleton columns={8} />
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                No users match the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-2 w-6">
                        <input
                          type="checkbox"
                          aria-label="Select all on this page"
                          checked={allSelected}
                          ref={(el) => {
                            if (el !== null) {el.indeterminate = someSelected;}
                          }}
                          onChange={toggleAll}
                          className="cursor-pointer"
                        />
                      </th>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">Role</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">KYC</th>
                      <th className="py-2 pr-4">Last login</th>
                      <th className="py-2 pr-4">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const isSelected = selected.has(row.id);
                      const isSuspended = row.status === "suspended";
                      return (
                        <tr
                          key={row.id}
                          className={cn(
                            "border-b last:border-b-0 hover:bg-muted/40",
                            isSelected && "bg-muted/30",
                          )}
                        >
                          <td className="py-3 pr-2">
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.full_name}`}
                              checked={isSelected}
                              disabled={isSuspended}
                              title={
                                isSuspended
                                  ? "Already suspended"
                                  : undefined
                              }
                              onChange={() => { toggleRow(row.id); }}
                              onClick={(e) => { e.stopPropagation(); }}
                              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </td>
                          <td
                            className="py-3 pr-4 font-medium cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                          >
                            {row.full_name}
                          </td>
                          <td
                            className="py-3 pr-4 font-mono text-xs cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                          >
                            {row.phone}
                          </td>
                          <td
                            className="py-3 pr-4 capitalize cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                          >
                            {row.primary_role}
                          </td>
                          <td
                            className="py-3 pr-4 cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                          >
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusPillClass(row.status)}`}
                            >
                              {row.status.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td
                            className="py-3 pr-4 text-xs cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                          >
                            {row.kyc_tier}
                          </td>
                          <td
                            className="py-3 pr-4 text-muted-foreground text-xs cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                            title={
                              row.last_login_at !== null
                                ? new Date(row.last_login_at).toLocaleString()
                                : undefined
                            }
                          >
                            {formatLastLogin(row.last_login_at)}
                          </td>
                          <td
                            className="py-3 pr-4 text-muted-foreground text-xs cursor-pointer"
                            onClick={() => { navigate(`/users/${row.id}`); }}
                          >
                            {new Date(row.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => { updateFilters({ offset: Math.max(0, offset - pageSize) }); }}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
                </span>
                <label className="flex items-center gap-1">
                  Rows:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      updateFilters({
                        size: Number.parseInt(e.target.value, 10),
                        offset: 0,
                      });
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => { updateFilters({ offset: offset + pageSize }); }}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}

// =============================================================================
// Create-admin modal
// =============================================================================
// Backed by POST /admin/users/create-admin (gated on user.role.assign,
// which super_admin always holds). The 0043 trigger auto-grants the
// super_admin role to the newly-created user on the primary_role='admin'
// INSERT, so the account works on its next admin-web login. After
// success, we navigate straight to /users/:id so the operator can
// pare back roles or add a specific custom role.
// =============================================================================
function CreateAdminModal({
  accessToken,
  onClose,
  onCreated,
}: {
  accessToken: string | null;
  onClose: () => void;
  onCreated: (detail: AdminUserDetail) => void;
}): React.JSX.Element {
  const [phone, setPhone] = useState<string>("+91");
  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  // AD-02. "admin" is a distinct backend endpoint with a distinct
  // permission, not a fifth option -- creating one auto-grants super_admin
  // via the 0043 trigger, which is not something to reach by scrolling a
  // dropdown.
  const [role, setRole] = useState<CreatableRole | "admin">("shipper");
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (accessToken === null) {return;}
    if (phone.trim().length < 10 || fullName.trim().length < 2) {
      setError("Phone must be at least 10 chars and full name at least 2.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const base = {
        phone: phone.trim(),
        full_name: fullName.trim(),
        ...(email.trim().length > 0 ? { email: email.trim() } : {}),
      };
      const detail =
        role === "admin"
          ? await usersAdminApi.createAdmin(accessToken, base)
          : await usersAdminApi.createUser(accessToken, {
              ...base,
              primary_role: role,
            });
      onCreated(detail);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
      } else {
        setError("Create failed. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create user</CardTitle>
          <CardDescription>
            Skips the signup screen, not verification — the person still
            signs in with an OTP on their own phone, and a driver still
            completes KYC before bidding.
            {role === "admin" ? (
              <span className="mt-1 block font-medium">
                An admin account auto-receives super_admin (migration 0043
                trigger). Pare the roles back on the user detail page.
              </span>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="ca-role">Role</Label>
              <select
                id="ca-role"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={role}
                disabled={busy}
                onChange={(e) => {
                  setRole(e.target.value as CreatableRole | "admin");
                }}
              >
                <option value="shipper">Shipper</option>
                <option value="driver">Driver</option>
                <option value="owner">Owner</option>
                <option value="agent">Agent</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <Label htmlFor="ca-phone">Phone (E.164)</Label>
              <Input
                id="ca-phone"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); }}
                placeholder="+91…"
                disabled={busy}
              />
            </div>
            <div>
              <Label htmlFor="ca-name">Full name</Label>
              <Input
                id="ca-name"
                value={fullName}
                onChange={(e) => { setFullName(e.target.value); }}
                placeholder="Rajesh Kumar"
                disabled={busy}
              />
            </div>
            <div>
              <Label htmlFor="ca-email">Email (optional)</Label>
              <Input
                id="ca-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
                placeholder="rajesh@vandiez.com"
                disabled={busy}
              />
            </div>
            {error !== null && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? "Creating…" : "Create user"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Bulk-suspend modal
// =============================================================================
// Confirms the count, captures a single shared reason (required by the
// backend audit trail), and posts to /admin/users/bulk-suspend. The
// page handles the toast + refresh from the response.
// =============================================================================
function BulkSuspendModal({
  accessToken,
  userIds,
  previewNames,
  onClose,
  onDone,
}: {
  accessToken: string | null;
  userIds: string[];
  previewNames: string[];
  onClose: () => void;
  onDone: (res: BulkSuspendUsersResponse) => void;
}): React.JSX.Element {
  const [reason, setReason] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (accessToken === null || userIds.length === 0) {return;}
    if (reason.trim().length < 4) {
      setError("Reason must be at least 4 characters (it goes into the audit log).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await usersAdminApi.bulkSuspend(accessToken, {
        user_ids: userIds,
        reason: reason.trim(),
      });
      onDone(res);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
      } else {
        setError("Bulk suspend failed. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Suspend {userIds.length} users</CardTitle>
          <CardDescription>
            Each suspended user loses access on their next request. The
            reason is written to the audit log and shown to the user on
            their next login attempt.
            {previewNames.length > 0 && (
              <span className="mt-2 block text-xs text-foreground">
                {previewNames.join(", ")}
                {userIds.length > previewNames.length ? ", …" : ""}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="bs-reason">Reason (4–500 chars)</Label>
              <Textarea
                id="bs-reason"
                value={reason}
                onChange={(e) => { setReason(e.target.value); }}
                maxLength={500}
                placeholder="e.g. Fraud cluster #421 — see Linear ticket OPS-820."
                disabled={busy}
                rows={3}
              />
            </div>
            {error !== null && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={busy}>
                {busy ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Suspending…
                  </>
                ) : (
                  <>
                    <Ban className="mr-1 h-4 w-4" />
                    Suspend {userIds.length}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
